import { useState } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { useChangePassword } from '../../lib/api/queries';

/**
 * Slice 15 — full-screen page shown by App.tsx whenever the current user's
 * {@code mustChangePassword} flag is true. The flag is set the moment a
 * DPAS-staff admin creates the account (or resets its password), so this
 * is the very first surface a client user sees after their initial login.
 *
 * The submit path calls POST /api/auth/change-password. Server clears the
 * flag on success and re-emits the auth cookie via RefreshSignInAsync;
 * useChangePassword promotes the returned user into the query cache so
 * App.tsx's gate re-renders — either the MFA setup page (for a brand-new
 * account) or the main app (for a returning user whose password was
 * admin-reset).
 */
export function ChangePasswordPage() {
  const [current, setCurrent] = useState('');
  const [next,    setNext]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const change = useChangePassword();

  const passesPolicy = policyChecks(next);
  const canSubmit =
    !change.isPending &&
    current.length > 0 &&
    next.length > 0 &&
    next === confirm &&
    Object.values(passesPolicy).every(Boolean);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (next !== confirm) {
      setLocalError('The two new-password fields don\'t match.');
      return;
    }
    change.mutate({ currentPassword: current, newPassword: next });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <ShieldCheck className="w-8 h-8 text-brand-600" aria-hidden />
          <span className="text-2xl font-semibold text-brand-700">DpasSuite</span>
        </div>

        <form onSubmit={onSubmit}
          className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-brand-600" aria-hidden />
            <h1 className="text-lg font-semibold text-gray-900">Pick a new password</h1>
          </div>

          <p className="text-sm text-gray-600">
            Your account was set up with a temporary password. Choose a
            password you'll remember before continuing.
          </p>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Current (temporary) password</span>
            <input
              type="password"
              autoComplete="current-password"
              required autoFocus
              value={current}
              onChange={e => setCurrent(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md
                         focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">New password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={e => setNext(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md
                         focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            <PolicyHints checks={passesPolicy} />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md
                         focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            {confirm.length > 0 && next !== confirm && (
              <p className="mt-1 text-xs text-danger-500">Passwords don't match.</p>
            )}
          </label>

          {(localError || change.isError) && (
            <p role="alert" className="text-sm text-danger-500">
              {localError ?? errorMessage(change.error)}
            </p>
          )}

          <button type="submit" disabled={!canSubmit}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2
                       bg-brand-600 text-white rounded-md hover:bg-brand-700
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <KeyRound className="w-4 h-4" aria-hidden />
            {change.isPending ? 'Changing…' : 'Change password'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Policy hints — mirror the server's Identity password options. Live
// feedback so the user sees rules light up as they type; kept in sync with
// AddDpasPlatformIdentity's PasswordOptions.
// ---------------------------------------------------------------------------

type PolicyChecks = {
  length:  boolean;
  digit:   boolean;
  lower:   boolean;
  upper:   boolean;
};

function policyChecks(pw: string): PolicyChecks {
  return {
    length: pw.length >= 8,
    digit:  /\d/.test(pw),
    lower:  /[a-z]/.test(pw),
    upper:  /[A-Z]/.test(pw),
  };
}

function PolicyHints({ checks }: { checks: PolicyChecks }) {
  const rows: Array<[keyof PolicyChecks, string]> = [
    ['length', 'At least 8 characters'],
    ['upper',  'An uppercase letter'],
    ['lower',  'A lowercase letter'],
    ['digit',  'A digit'],
  ];
  return (
    <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
      {rows.map(([key, label]) => (
        <li key={key} className={checks[key] ? 'text-success-500' : 'text-gray-500'}>
          {checks[key] ? '✓' : '•'} {label}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Error mapping — the endpoint returns 400 with a string body for both bad-
// current-password and policy failures; surface that verbatim.
// ---------------------------------------------------------------------------

function errorMessage(err: unknown): string {
  const status = (err as { status?: number })?.status;
  const body   = (err as { body?: unknown })?.body;
  if (status === 400 && typeof body === 'string') return body;
  if (status === 401) return 'Session expired — please sign in again.';
  return 'Couldn\'t change your password. Try again.';
}
